/**
 * AdsManager API Service
 * Handles all API calls for the Ads Manager
 */

import api from './api';
import { Ad } from '../types/ads.types';

export interface FetchAdsParams {
  fromDate: string;
  toDate: string;
  enrich?: boolean; // Whether to fetch creative thumbnails
  compress?: boolean; // Whether to use gzip compression
  page?: number; // For pagination
  pageSize?: number; // Items per page
}

export interface FetchAdsResponse {
  data?: Ad[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
  loadTimeMs?: number;
}

export interface UpdateAdStatusRequest {
  status: 'ACTIVE' | 'PAUSED';
}

export interface BatchStatusRequest {
  statusUpdates: Array<{ adId: string; newStatus: 'ACTIVE' | 'PAUSED' }>;
}

export interface UpdateAdsetBudgetRequest {
  budget: number;
}

export interface BatchBudgetRequest {
  adsetBudgets: Record<string, number>;
}

export interface FetchCreativesRequest {
  creativeIds: string[];
}

export interface FetchCreativesResponse {
  success: boolean;
  creatives: Record<string, string>;
  totalRequested: number;
  totalFetched: number;
  loadTimeMs: number;
  cacheHitRate: number;
}

export interface DuplicateAdRequest {
  budgetMultiplier?: number;
  nameSuffix?: string;
  newName?: string;
  status?: 'ACTIVE' | 'PAUSED';
  createNewAdset?: boolean;
  newAdsetName?: string;
  bidStrategy?: string;
  bidAmount?: number;
  minRoasTarget?: number;
}

export interface DuplicateAdResponse {
  success: boolean;
  message: string;
  originalAdId: string;
  newAdId: string;
  originalName: string;
  newName: string;
  budgetMultiplier: number;
  createdNewAdset: boolean;
  newAdsetId?: string;
  timestamp: string;
}

export interface AdFullDetails {
  success: boolean;
  ad: {
    id: string;
    name: string;
    status: string;
    creative?: {
      id: string;
      thumbnail_url?: string;
      title?: string;
      body?: string;
      image_url?: string;
    };
  };
  adset: {
    id: string;
    name: string;
    daily_budget?: string;
    bid_strategy?: string;
    bid_amount?: string;
    optimization_goal?: string;
  };
  campaign: {
    id: string;
    name: string;
  };
}

/**
 * AdsManager Service Class
 * Provides methods for interacting with the Facebook Ads Manager API
 */
class AdsManagerService {
  private readonly baseUrl = '/v1/facebook';

  /**
   * Fetch unified data set (UDS) from the backend
   * Combines internal stats with Facebook metadata
   *
   * Endpoint: GET /facebook/uds
   * Old system: GET /api/fb-ads-manager-async-uds
   * New system: GET /api/v1/facebook/uds (api instance adds /api/v1 prefix)
   */
  async fetchAds(params: FetchAdsParams): Promise<Ad[]> {
    const response = await api.get<any>(`${this.baseUrl}/uds`, {
      params: {
        fromDate: params.fromDate,
        toDate: params.toDate,
        enrich: params.enrich !== false ? 'true' : 'false',
        compress: params.compress ? 'true' : undefined,
        page: params.page,
        pageSize: params.pageSize,
      },
      timeout: 300000, // 5 minutes
    });

    let rawAds: any[] = [];

    // Handle paginated response
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      rawAds = (response.data as FetchAdsResponse).data || [];
    } else {
      // Handle legacy response (array)
      rawAds = response.data || [];
    }

    // Transform backend structure to frontend Ad interface
    return rawAds.map((rawAd) => this.transformAdData(rawAd));
  }

  /**
   * Get the API base URL for constructing absolute URLs
   */
  private getApiBaseUrl(): string {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';
  }

  /**
   * Transform backend ad structure to frontend Ad interface
   * Backend returns totals object with aggregated metrics
   * Frontend expects flat structure with top-level metrics
   */
  private transformAdData(rawAd: any): Ad {
    const totals = rawAd.totals || {};
    const apiBaseUrl = this.getApiBaseUrl();

    // Make creative image URLs absolute
    const facebookAd = rawAd.facebookAd
      ? {
          ...rawAd.facebookAd,
          creative: rawAd.facebookAd.creative
            ? {
                ...rawAd.facebookAd.creative,
                thumbnail_url: (() => {
                  const originalUrl = rawAd.facebookAd.creative.thumbnail_url;
                  if (!originalUrl) return null;
                  if (originalUrl.startsWith('http')) return originalUrl;
                  const absoluteUrl = `${apiBaseUrl.replace(/\/api$/, '')}${originalUrl}`;
                  return absoluteUrl;
                })(),
              }
            : null,
        }
      : null;

    // Transform daily breakdown structure
    const dailyBreakdown = (rawAd.dailyBreakdown || []).map((day: any) => {
      const performance = day.performance || {};
      const landingPageMetrics = day.landingPageMetrics || {};

      const spend = performance.spend || 0;
      const revenue = performance.revenue || 0;
      const clicks = landingPageMetrics.reportedClicks || 0;
      const searches = landingPageMetrics.actualSearches || 0;
      const monetizedSearches = landingPageMetrics.reportedMonetizedSearches || 0;

      return {
        date: day.date,
        spend,
        revenue,
        clicks,
        searches,
        roas: performance.roas || 0,
        rpm: searches > 0 ? (revenue / searches) * 1000 : 0,
        rpc: clicks > 0 ? revenue / clicks : 0,
        cpc: clicks > 0 ? spend / clicks : 0,
        cpa: 0, // Not calculated in daily breakdown
        pageCtr: searches > 0 ? (clicks / searches) * 100 : 0,
        coverage: searches > 0 ? (monetizedSearches / searches) * 100 : 0,
      };
    });

    // Transform hourly breakdown structure
    const hourlyBreakdown = (rawAd.hourlyBreakdown || []).map((hour: any) => {
      const performance = hour.performance || {};
      const landingPageMetrics = hour.landingPageMetrics || {};

      const spend = performance.spend || 0;
      const revenue = performance.revenue || 0;
      const clicks = landingPageMetrics.reportedClicks || 0;
      const searches = landingPageMetrics.actualSearches || 0;

      return {
        hour: hour.hour,
        date: hour.date,
        spend,
        revenue,
        clicks,
        searches,
        roas: performance.roas || 0,
      };
    });

    // Extract metrics from totals
    const spend = totals.totalSpend || 0;
    const revenue = totals.totalRevenue || 0;
    const clicks = totals.totalClicks || 0;
    const searches = totals.totalSearches || 0;

    // Extract adset budget from Facebook metadata
    const adsetDailyBudget = rawAd.facebookAdset?.daily_budget
      ? parseFloat(rawAd.facebookAdset.daily_budget)
      : undefined;

    return {
      // Identifiers
      adId: rawAd.adId,
      adsetId: rawAd.adsetId,
      campaignId: rawAd.campaignId,
      accountId: rawAd.accountId,

      // Names
      adName: rawAd.adName,
      adsetName: rawAd.adsetName,
      campaignName: rawAd.campaignName,
      advertiserName: rawAd.advertiserName,

      // Status
      status: rawAd.status || rawAd.effectiveStatus || 'UNKNOWN',

      // Budget
      adsetDailyBudget,

      // Aggregated metrics from totals
      spend,
      revenue,
      pnl: totals.totalProfitLoss || 0,
      roas: totals.overallROAS || 0,
      rpm: searches > 0 ? (revenue / searches) * 1000 : 0,
      rpc: clicks > 0 ? revenue / clicks : 0,
      cpa: 0, // Not provided in backend response
      cpc: clicks > 0 ? spend / clicks : 0,
      clicks,
      searches,
      pageCtr: searches > 0 ? (clicks / searches) * 100 : 0,
      coverage: 0, // Not provided in backend response

      // Dates
      adCreatedTime: rawAd.ad_created_time,
      campaignCreatedTime: rawAd.campaign_created_time,
      adUpdatedTime: rawAd.updated_time,

      // Breakdowns (transformed)
      dailyBreakdown,
      hourlyBreakdown: hourlyBreakdown.length > 0 ? hourlyBreakdown : undefined,

      // Facebook metadata (with absolute image URLs)
      facebookAd,
      facebookAdset: rawAd.facebookAdset,
      facebookCampaign: rawAd.facebookCampaign,
    };
  }

  /**
   * Update ad status (single ad)
   * Endpoint: POST /api/v1/facebook/ad/:id/status
   */
  async updateAdStatus(adId: string, status: 'ACTIVE' | 'PAUSED'): Promise<void> {
    await api.post(`${this.baseUrl}/ad/${adId}/status`, { status });
  }

  /**
   * Update ad status (batch)
   * Endpoint: POST /api/v1/facebook/ads/batch-status-optimized
   */
  async batchUpdateAdStatuses(request: BatchStatusRequest): Promise<void> {
    await api.post(`${this.baseUrl}/ads/batch-status-optimized`, request);
  }

  /**
   * Update adset budget (single adset)
   * Endpoint: POST /api/v1/facebook/adset/:id/budget
   */
  async updateAdsetBudget(adsetId: string, budget: number): Promise<void> {
    await api.post(`${this.baseUrl}/adset/${adsetId}/budget`, { budget });
  }

  /**
   * Update adset budget (batch)
   * Endpoint: POST /api/v1/facebook/adsets/batch-budget-optimized
   */
  async batchUpdateAdsetBudgets(request: BatchBudgetRequest): Promise<void> {
    await api.post(`${this.baseUrl}/adsets/batch-budget-optimized`, request);
  }

  /**
   * Fetch creative images (batch)
   * Endpoint: POST /api/v1/facebook/creatives
   * Old system: POST /api/ads-manager/creatives
   * New system: POST /api/v1/facebook/creatives
   *
   * Progressive image loading - fetches creative thumbnails in batches
   * Uses three-tier caching:
   *   TIER 1: facebook_images_cache (GCS cached images)
   *   TIER 2: account_snapshot (fallback to FB CDN URLs)
   *   TIER 3: Facebook API (fallback to FB CDN)
   */
  async fetchCreatives(creativeIds: string[]): Promise<Record<string, string>> {
    const response = await api.post<FetchCreativesResponse>(
      `${this.baseUrl}/creatives`,
      { creativeIds },
      { timeout: 60000 }, // 1 minute
    );
    return response.data.creatives || {};
  }

  /**
   * Get full ad details for duplication wizard
   * Endpoint: GET /api/v1/facebook/ad/:id/full-details
   */
  async getAdFullDetails(adId: string): Promise<AdFullDetails> {
    const response = await api.get<AdFullDetails>(`${this.baseUrl}/ad/${adId}/full-details`);
    return response.data;
  }

  /**
   * Duplicate an ad with optional configuration
   * Endpoint: POST /api/v1/facebook/ad/:id/duplicate
   */
  async duplicateAd(adId: string, request: DuplicateAdRequest): Promise<DuplicateAdResponse> {
    const response = await api.post<DuplicateAdResponse>(
      `${this.baseUrl}/ad/${adId}/duplicate`,
      request,
    );
    return response.data;
  }
}

// Export singleton instance
export const adsManagerService = new AdsManagerService();
export default adsManagerService;
